import { useMemo, useState } from "react"

import { useGetMessageStats } from "@/api/generated/messaging/messaging"

import { useListApiKeys } from "@/api/generated/apikeys/apikeys"
import { useGetSubscription } from "@/api/generated/billing/billing"
import { useListDevices } from "@/api/generated/devices/devices"
import { useListMessages } from "@/api/generated/messaging/messaging"
import { useGetOverview } from "@/api/generated/ops/ops"
import { useListTopups } from "@/api/generated/topups/topups"
import { STALE } from "@/api/query-client"
import { useMe } from "@/auth/session"
import { isMoneyVisible } from "@/auth/permissions"
import { enumFilter, stringFilter, useUrlFilters } from "@/lib/url-state"
import { previousRange, rangeFromUrl, toOverviewParams, type OverviewRange } from "../lib/overview"
import { alignByDayOffset } from "../lib/traffic"

const SPEC = {
  range: enumFilter(["7d", "30d", "90d", "custom"] as const, "30d"),
  from: stringFilter(),
  to: stringFilter(),
}

/** The overview's window, kept in the URL (`range`, `from`, `to`). */
export function useOverviewRange(): [OverviewRange, (next: OverviewRange) => void] {
  const { filters, setFilters } = useUrlFilters(SPEC)
  const range = rangeFromUrl(filters)
  const set = (next: OverviewRange) => {
    setFilters(
      next.range === "custom"
        ? { range: "custom", from: next.from, to: next.to }
        : { range: next.range, from: "", to: "" },
    )
  }
  return [range, set]
}

/**
 * What the caller may ask (LLD §9.3): each query runs only with its
 * permission, and money only in normal mode, so a region the caller cannot
 * read is never requested (it would 403 on every visit).
 */
export function useOverviewAccess() {
  const { data: me } = useMe()
  return useMemo(() => {
    const held = new Set(me?.permissions ?? [])
    const money = isMoneyVisible(me?.mode)
    return {
      figures: held.has("account.profile:read"),
      messages: held.has("messaging.sms:read"),
      devices: held.has("devices.device:read"),
      keys: held.has("apikeys.key:manage"),
      usage: held.has("billing.subscription:read"),
      wallet: money && held.has("wallet.balance:read"),
      money,
    }
  }, [me])
}

export const useOverview = (range: OverviewRange, enabled: boolean) =>
  useGetOverview(toOverviewParams(range), { query: { enabled } })

export const useUsageCard = (enabled: boolean) =>
  useGetSubscription({ query: { enabled, staleTime: STALE.content } })

/** The first page of phones (the bell's query: same key, one request). */
export const useFirstDevices = (enabled: boolean) =>
  useListDevices({ limit: 100 }, { query: { enabled } })

/** Whether anything exists yet, for the checklist: one item is enough. */
export const useAnyKey = (enabled: boolean) =>
  useListApiKeys({ status: "all", limit: 1 }, { query: { enabled, staleTime: STALE.reference } })
export const useAnyMessage = (enabled: boolean) =>
  useListMessages({ limit: 1 }, { query: { enabled } })

export const useTopups = (enabled: boolean) => useListTopups({ limit: 100 }, { query: { enabled } })

const DAY = 86_400_000
const NAMED_DAYS = { "7d": 7, "30d": 30, "90d": 90 } as const

/**
 * The chart's window, as the backend reads the range (ops `ParseRange`):
 * named ranges end now, rounded to the minute and fixed per mount (as the
 * bell's 24 h window) so the query key does not move on every render.
 */
export function useTrafficWindow(range: OverviewRange): { from: string; to: string } {
  const [now] = useState(() => Math.floor(Date.now() / 60_000) * 60_000)
  const key = JSON.stringify(range)
  return useMemo(() => {
    const r = JSON.parse(key) as OverviewRange
    if (r.range === "custom") {
      const p = toOverviewParams(r)
      return { from: p.from ?? "", to: p.to ?? "" }
    }
    return {
      from: new Date(now - NAMED_DAYS[r.range] * DAY).toISOString(),
      to: new Date(now).toISOString(),
    }
  }, [key, now])
}

/** `getMessageStats` per day for the window and the one before it, aligned day by day. */
export function useTraffic(window: { from: string; to: string }, enabled: boolean) {
  const before = previousRange(window)
  const current = useGetMessageStats({ ...window, bucket: "day" }, { query: { enabled } })
  const previous = useGetMessageStats({ ...before, bucket: "day" }, { query: { enabled } })
  return {
    current,
    days: current.data ? alignByDayOffset(current.data.buckets, previous.data?.buckets) : undefined,
  }
}
